import { Controller } from "react-hook-form";
import { InfoBox } from "~/components/Backroom/InfoBox";
import Button from "~/components/Button";
import { FileUpload } from "~/components/Backroom/FileUpload";
import Icon from "~/assets/icons/Icon";
import { useUserQuery } from "~/api/user";
import { CurrentStepProps } from "../getSideMenuItems";
import { Inputs } from "../useUpsertProductForm";
import { FeaturedImageAspectPicker } from "./FeaturedImageAspectPicker";
import classNames from "classnames";
import { getAspectRatioClass } from "~/utils/getAspectRatioClass";

export const photosPreviewImages = [
  {
    id: "photos1",
    title: "Vist på produktsiden",
    src: "/assets/images/previews/products/product-image/desktop.png",
    alt: "Placering af billedet på produktsiden",
    width: 340,
    height: 420,
  },
  {
    id: "photos2",
    title: "Vist på produktsiden på mobil",
    src: "/assets/images/previews/products/product-image/mobil.png",
    alt: "Placering af billedet på produktsiden på mobil",
    width: 200,
    height: 500,
  },
  {
    id: "photos3",
    title: "Vist på produktoverbliksiden",
    src: "/assets/images/previews/products/product-image/desktop2.png",
    alt: "Placering af billedet på produktoverbliksiden",
    width: 340,
    height: 420,
  },
  {
    id: "photos4",
    title: "Vist på produktoverbliksiden på mobil",
    src: "/assets/images/previews/products/product-image/mobil2.png",
    alt: "Placering af billedet på produktoverbliksiden på mobil",
    width: 200,
    height: 500,
  },
];

export const FIELDS: Array<keyof Inputs> = [
  "images",
  "featuredImage",
  "otherImages",
  "featuredImageOrientation",
  "video",
];

export const PhotosStep = (props: CurrentStepProps) => {
  const {
    form: { control, watch },
    isEditing,
  } = props;

  const featuredImageOrientation = watch("featuredImageOrientation");

  const { data: user } = useUserQuery();
  const isStorePublished = Boolean(user?.store?.publishedAt);

  return (
    <>
      <h1 className="text-3xl mb-5">Fotos og video af produktet</h1>
      <p className="mb-12">
        Tilføj billeder og eventuelt en kort video af dit produkt.
      </p>

      <div className="flex flex-col gap-y-1.5">
        <div className="mb-6">
          <h2 className="text-2xl mb-2.5">Fremhævet billede</h2>
          <p className="text-secondary-800">
            Vælg ét fremhævet billede.
            <br />
            Du kan vælge format på billedet, efter det er uploadet.
          </p>
        </div>
        <Controller
          control={control}
          name="featuredImage"
          rules={{ required: "Du skal tilføje et fremhævet billede" }}
          render={({
            field: { value, onChange, ...field },
            fieldState: { error },
          }) => {
            return (
              <FileUpload
                {...field}
                id="featuredImage"
                label="Tilføj et fremhævet billede:"
                initialFiles={value}
                onChange={onChange}
                browseText="Tilføj billedfil for at uploade eller"
                error={error}
                required={true}
                acceptedFileTypes={["image/*"]}
                showPrimary
                aspectRatioClass={classNames(
                  "object-cover",
                  getAspectRatioClass(value?.[0], featuredImageOrientation)
                )}
                hideBackgroundMedia
              />
            );
          }}
        />

        <FeaturedImageAspectPicker form={props.form} className="mt-8" />

        <div className="mb-6 mt-12">
          <h2 className="text-2xl mb-2.5">Produktbilleder</h2>
          <p className="text-secondary-800">
            Du kan tilføje 9 ekstra produktbilleder.
            <br />
            Billederne vil automatisk blive tilpasset i størrelsen. Tryk "Se
            eksempel" under billedet for at se hvordan det vil tage sig ud på
            produktsiden. Her kan du også vælge den rækkefølge billederne skal
            komme i.
          </p>
        </div>
        <Controller
          control={control}
          name="otherImages"
          render={({
            field: { value, onChange, ...field },
            fieldState: { error },
          }) => {
            return (
              <FileUpload
                {...field}
                id="otherImages"
                label="Tilføj produktbilleder og vælg rækkefølge:"
                initialFiles={value}
                onChange={onChange}
                browseText="Tilføj billedfil for at uploade eller"
                error={error}
                fileLimit={9}
                enableReorder
                acceptedFileTypes={["image/*"]}
              />
            );
          }}
        />

        <div className="mb-6 mt-12">
          <h2 className="text-2xl mb-2.5">Produktvideo</h2>
          <p className="text-secondary-800">
            Du kan tilføje én kort video af dit produkt (maks. 10 sekunder, maks. 50 MB).
            <br />
            Videoen vises i galleriet på produktsiden. Optag i mp4-format fra din mobiltelefon.
          </p>
        </div>
        <Controller
          control={control}
          name="video"
          render={({
            field: { value, onChange, ...field },
            fieldState: { error },
          }) => {
            return (
              <FileUpload
                {...field}
                id="video"
                label="Tilføj en produktvideo (valgfrit):"
                initialFiles={value ?? undefined}
                onChange={(files) => onChange(files ?? [])}
                browseText="Tilføj videofil for at uploade eller"
                error={error}
                fileLimit={1}
                acceptedFileTypes={["video/*"]}
                sizeLimitMB={50}
                maxVideoDurationSeconds={10}
                acceptedMimeTypes={["video/mp4"]}
              />
            );
          }}
        />
      </div>

      <div className="flex flex-col-reverse lg:flex-col gap-12 mt-12">
        <InfoBox>
          <ul className="list-disc pl-6">
            <li>
              Hvis du har behov for hjælp til produktfotos, har Butik Danmark
              gode aftaler med fotografer rundt om i landet.
            </li>
            {!isEditing && (
              <>
                <li>
                  Hvis du vælger "Gem som kladde", vil dit produkt blive gemt,
                  men det vil ikke blive vist i din butik.
                </li>
                <li>
                  Hvis du vælger "Gem og publicér", vil produktet blive gemt, og
                  det vil blive fremvist i din butik.
                </li>
              </>
            )}
          </ul>
        </InfoBox>
        <div>
          <PhotosStepButtonGroup
            {...props}
            isStorePublished={isStorePublished}
          />
          {!isStorePublished ? (
            <small className="block text-sm text-secondary-500 mt-2 text-right">
              Din butik skal godkendes, før du kan publicere dit produkt.
            </small>
          ) : null}
        </div>
      </div>
    </>
  );
};

const PhotosStepButtonGroup = (
  props: CurrentStepProps & { isStorePublished?: boolean }
) => {
  const {
    stepId,
    form: {
      formState: { isDirty, isValid },
      reset,
      watch,
      trigger,
    },
    onContinue,
    isEditing,
    onStepChange,
    isSubmitting,
    isStorePublished,
  } = props;
  const featuredImage = watch("featuredImage");

  const beforeContinue = async (options?: {
    shouldExitEditing?: boolean;
    action?: "draft" | "publish";
  }) => {
    const isValid = await trigger(FIELDS);

    if (isValid) {
      onContinue(stepId, { visibleFields: FIELDS, ...options });
      isEditing && reset({}, { keepValues: true });
    }
  };

  if (isEditing && onStepChange) {
    return (
      <div className={`flex flex-col md:flex-row gap-5`}>
        <Button
          variant="primary-light"
          size="lg"
          className="flex items-center gap-2.5 justify-center"
          onClick={() => onStepChange("previous")}
          disabled={isDirty || !isValid}
        >
          <Icon name="arrowLeft" className="w-6 h-6" />
          Forrige
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            beforeContinue({ shouldExitEditing: true });
          }}
          disabled={featuredImage?.length === 0 || isSubmitting}
        >
          Gem og afslut
        </Button>
      </div>
    );
  }

  if (isStorePublished) {
    return (
      <div className={`flex flex-col md:flex-row gap-5`}>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            beforeContinue({ action: "draft" });
          }}
          disabled={featuredImage?.length === 0 || isSubmitting || !isValid}
        >
          Gem som kladde
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            beforeContinue({ action: "publish" });
          }}
          disabled={featuredImage?.length === 0 || isSubmitting || !isValid}
        >
          Gem og publicér
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="primary"
      size="lg"
      onClick={() => {
        beforeContinue({ action: "draft" });
      }}
      disabled={featuredImage?.length === 0 || isSubmitting || !isValid}
    >
      Gem som kladde
    </Button>
  );
};
